import { createRef, useEffect, useState } from "react";
import { Form, Formik, FormikContextType } from "formik";
import clsx from "clsx";

// Components từ hệ thống của bạn
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import Button from "@app/components/button";

import useObservable from "@core/hooks/use-observable.hook";
import AuthService from "@app/services/http/auth.service";
import { changePasswordValidationSchema } from "@app/validations";
import { openPortalDialog } from "@app/services/modal.service";
import ConfirmModal from "@app/components/confirm-modal";

interface ChangePasswordFormValues {
  password: string;
  confirmPassword: string;
}

function ChangePasswordPage() {
  const formRef = createRef<FormikContextType<ChangePasswordFormValues>>();
  const { subscribeOnce } = useObservable();

  const [token, setToken] = useState<string>("");
  const [serverMessage, setServerMessage] = useState<string>("");
  const [statusType, setStatusType] = useState<"error" | "success" | "">("");

  // Lấy token từ URL (?token=abc...) khi component được mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get("token") || "";

    setToken(tokenParam);

    if (!tokenParam) {
      setServerMessage(
        "Đường dẫn thiếu mã xác thực (Token). Vui lòng kiểm tra lại Email!"
      );
      setStatusType("error");
    }
  }, []);

  const handleChangePassword = (values: ChangePasswordFormValues) => {
    if (!token) {
      setServerMessage("Không tìm thấy mã token hợp lệ!");
      setStatusType("error");

      return;
    }

    setServerMessage(""); // Clear thông báo cũ
    setStatusType("");

    // Đặt lại mật khẩu bằng token trong link email (token gửi trong body, chỉ dùng được 1 lần)
    subscribeOnce(
      AuthService.resetPassword(values.password, token),
      (res: any) => {
        openPortalDialog(ConfirmModal, {
          message: res.message,
        });
        setServerMessage(res.message);

        if (res.statusCode === 200 || res.success || res.ok) {
          setStatusType("success");
          // Chuyển hướng về trang đăng nhập sau 1 giây
          setTimeout(() => {
            window.location.href = "https://pcrender.com/login";
          }, 1000);
        } else {
          setStatusType("error");
        }
      }
    );
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 relative overflow-hidden flex items-center justify-center p-4 sm:p-6 font-sans">
      {/* Visual background gradient accents for light theme */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-100/60 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-100/60 rounded-full blur-[100px] pointer-events-none" />

      {/* Main Container Card */}
      <div className="relative w-full max-w-[440px] bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-8 shadow-xl shadow-slate-200/50 transition-all duration-300">
        {/* Header Section / Brand Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-500 p-0.5 mb-3 shadow-md shadow-blue-500/20">
            <div className="w-full h-full bg-white rounded-[14px] flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            PC Render
          </h1>
          <p className="text-slate-500 text-sm font-medium mt-1">
            Thay đổi mật khẩu tài khoản
          </p>
        </div>

        {/* Hộp lưu ý thời gian hiệu lực */}
        <div className="bg-amber-50 border border-amber-200/80 rounded-2xl p-3.5 mb-6 flex items-start gap-3">
          <div className="p-1 bg-amber-100 rounded-lg text-amber-700 shrink-0 mt-0.5">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-amber-900 text-xs font-normal leading-relaxed">
            <span className="font-semibold text-amber-800">Chú ý:</span> Form
            thay đổi mật khẩu này chỉ có hiệu lực trong vòng{" "}
            <span className="font-semibold underline underline-offset-2">
              2 phút
            </span>
            .
          </p>
        </div>

        {/* Formik Form */}
        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          innerRef={formRef}
          onSubmit={handleChangePassword}
          validationSchema={changePasswordValidationSchema}
          validateOnChange
          validateOnBlur
        >
          {({ isSubmitting }) => (
            <Form className="space-y-5 text-left">
              {/* Ô nhập mật khẩu mới */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Mật khẩu mới
                </label>
                <FormControl name="password">
                  <Input
                    type="password"
                    className="!max-w-none text-sm !h-11 rounded-xl bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all duration-200"
                    errorClassName="text-xs mt-1.5 text-red-500 font-medium"
                    placeholder="Nhập mật khẩu mới"
                    disabled={isSubmitting || statusType === "success"}
                  />
                </FormControl>
              </div>

              {/* Ô xác nhận mật khẩu mới */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Xác nhận mật khẩu mới
                </label>
                <FormControl name="confirmPassword">
                  <Input
                    type="password"
                    className="!max-w-none text-sm !h-11 rounded-xl bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all duration-200"
                    errorClassName="text-xs mt-1.5 text-red-500 font-medium"
                    placeholder="Nhập lại mật khẩu mới"
                    disabled={isSubmitting || statusType === "success"}
                  />
                </FormControl>
              </div>

              {/* Hiển thị lỗi thông báo từ hệ thống / server */}
              {serverMessage && (
                <div
                  className={clsx(
                    "text-xs font-medium text-center p-3 rounded-xl border transition-all duration-200 flex items-center justify-center gap-2",
                    statusType === "success" &&
                      "text-emerald-700 bg-emerald-50 border-emerald-200 font-semibold",
                    statusType === "error" &&
                      "text-red-700 bg-red-50 border-red-200",
                    statusType === "" &&
                      "text-slate-600 bg-slate-100 border-slate-200"
                  )}
                >
                  {serverMessage}
                </div>
              )}

              {/* Button Xác nhận submit form */}
              <div className="pt-2">
                <Button
                  type="submit"
                  label={
                    isSubmitting ? "Đang xử lý..." : "Xác nhận đổi mật khẩu"
                  }
                  disabled={isSubmitting || statusType === "success" || !token}
                  containerClassName="w-full"
                  width="100%"
                  className={clsx(
                    "w-full !h-11 text-sm font-semibold rounded-xl transition-all duration-200 shadow-md",
                    isSubmitting || statusType === "success" || !token
                      ? "!bg-slate-200 !text-slate-400 !border-slate-200 cursor-not-allowed shadow-none"
                      : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-blue-500/20 active:scale-[0.99]"
                  )}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default ChangePasswordPage;
